/**
 * OpenNebula VPC + VPC peering. Endpoints (v2 s10003):
 *   /vpcs, /vpcs/:id, /vpcs/:id/subnets, /vpcs/:id/subnets/:sid
 *   /peering/subnets, /peering/groups, /peering/groups/:id (+ add/remove vpc)
 */
import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from './_shared';

export interface VpcSubnet {
  id: string;
  name?: string;
  cidr?: string;
  description?: string;
  is_shared?: boolean;
  /** Detail-payload extras (the list shape omits these). */
  gateway?: string;
  status?: string;
  /** Address utilisation: leased of size. */
  leased?: number;
  size?: number;
  attached_count?: number;
}
/** The virtual router attached to a VPC (detail payload, sibling of `vpc`). */
export interface VpcRouter {
  id: string;
  name?: string;
  description?: string;
  status?: string;
  public_ip?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Vpc {
  id: string;
  name?: string;
  cidr?: string;
  description?: string;
  is_shared?: boolean;
  status?: string;
  created_at?: string;
  /** Subnet tally on the LIST payload (detail returns the full `subnets[]` instead). */
  subnet_count?: number;
  subnets?: VpcSubnet[];
  /** Egress router state + its external NAT public IP (returned on list + detail). */
  router_status?: string;
  router_public_ip?: string | null;
  /** Full attached router (detail payload only — merged in from the sibling `router`). */
  router?: VpcRouter | null;
}

/** Editable VPC fields (cidr is immutable). */
export interface UpdateVpcPayload {
  name?: string;
  description?: string;
  is_shared?: boolean;
}
/** Editable subnet fields (cidr is immutable). */
export interface UpdateSubnetPayload {
  name?: string;
  description?: string;
  is_shared?: boolean;
}

const arr = <T,>(body: { [k: string]: unknown } | T[], key: string): T[] =>
  Array.isArray(body) ? body : ((body as Record<string, unknown>)[key] as T[]) ?? [];

export function useVpcs(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.vpcs(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => arr<Vpc>(unwrap(await serviceApi.get(onPath(serviceId!, 'vpcs'))), 'vpcs'),
  });
}

export function useVpc(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: onKeys.vpc(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ vpc?: Vpc; subnets?: VpcSubnet[]; router?: VpcRouter | null } | Vpc>(await serviceApi.get(onPath(serviceId!, `vpcs/${id}`)));
      // Detail envelope is { vpc, subnets, router } — subnets and the attached router
      // are SIBLINGS of vpc, not nested. Merge them so consumers read them off vpc.
      if (body && typeof body === 'object' && 'vpc' in body && body.vpc) {
        return { ...(body.vpc as Vpc), subnets: body.subnets ?? body.vpc.subnets, router: body.router ?? body.vpc.router };
      }
      return body as Vpc;
    },
  });
}

export function useCreateVpc(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(onPath(serviceId, 'vpcs'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.vpcs(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useUpdateVpc(serviceId: string, id: string, options?: UseMutationOptions<unknown, unknown, UpdateVpcPayload>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: UpdateVpcPayload) => (await serviceApi.put(onPath(serviceId, `vpcs/${id}`), p)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.vpcs(serviceId) });
      qc.invalidateQueries({ queryKey: onKeys.vpc(serviceId, id) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteVpc(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `vpcs/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.vpcs(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useCreateSubnet(serviceId: string, vpcId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(onPath(serviceId, `vpcs/${vpcId}/subnets`), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.vpc(serviceId, vpcId) }); options?.onSuccess?.(...a); },
  });
}

export function useUpdateSubnet(serviceId: string, vpcId: string, sid: string, options?: UseMutationOptions<unknown, unknown, UpdateSubnetPayload>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: UpdateSubnetPayload) => (await serviceApi.patch(onPath(serviceId, `vpcs/${vpcId}/subnets/${sid}`), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.vpc(serviceId, vpcId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeleteSubnet(serviceId: string, vpcId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sid: string) => (await serviceApi.delete(onPath(serviceId, `vpcs/${vpcId}/subnets/${sid}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.vpc(serviceId, vpcId) }); options?.onSuccess?.(...a); },
  });
}

// ---- Peering subnets -----------------------------------------------------

export interface PeeringSubnet {
  id: string;
  name?: string;
  description?: string;
  /** Subnet CIDR / base IP (API field is `ip_range`). */
  ip_range?: string;
  vlan_id?: string | number;
  status?: string;
  created_at?: string;
}

export function usePeeringSubnets(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.peeringSubnets(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    staleTime: 30_000,
    queryFn: async () =>
      arr<PeeringSubnet>(unwrap(await serviceApi.get(onPath(serviceId!, 'peering/subnets'))), 'peering_subnets'),
  });
}

export function useCreatePeeringSubnet(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(onPath(serviceId, 'peering/subnets'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.peeringSubnets(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeletePeeringSubnet(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `peering/subnets/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.peeringSubnets(serviceId) }); options?.onSuccess?.(...a); },
  });
}

// ---- Peering groups ------------------------------------------------------

/** A VPC attached to a peering group. `vpc_id` is the join key; `interface_ip`
 *  (a.k.a. `peering_ip`) is the router's IP on the transit subnet. */
export interface PeeringMember {
  id?: string;
  vpc_id: string;
  vpc_name?: string;
  interface_ip?: string;
  peering_ip?: string;
  status?: string;
}

export interface PeeringGroup {
  id: string;
  name?: string;
  status?: string;
  created_at?: string;
  /** Transit subnet this group routes through. */
  peering_subnet_id?: string;
  peering_subnet?: { id: string; name?: string; ip_range?: string };
  /** Member VPCs (API key is `members`, each carries `vpc_id`). */
  members?: PeeringMember[];
}

export function usePeeringGroups(serviceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.peeringGroups(serviceId ?? ''),
    enabled: !!serviceId,
    retry: false,
    queryFn: async () => arr<PeeringGroup>(unwrap(await serviceApi.get(onPath(serviceId!, 'peering/groups'))), 'peering_groups'),
  });
}

export function useCreatePeeringGroup(serviceId: string, options?: UseMutationOptions<unknown, unknown, Record<string, unknown>>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: Record<string, unknown>) => (await serviceApi.post(onPath(serviceId, 'peering/groups'), p)).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.peeringGroups(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useDeletePeeringGroup(serviceId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await serviceApi.delete(onPath(serviceId, `peering/groups/${id}`))).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.peeringGroups(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function usePeeringGroup(serviceId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: onKeys.peeringGroup(serviceId ?? '', id ?? ''),
    enabled: !!serviceId && !!id,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ peering_group?: PeeringGroup } | PeeringGroup>(await serviceApi.get(onPath(serviceId!, `peering/groups/${id}`)));
      return (body && typeof body === 'object' && 'peering_group' in body ? body.peering_group : body) as PeeringGroup;
    },
  });
}

export function useAddVpcToGroup(serviceId: string, groupId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vpcId: string) => (await serviceApi.post(onPath(serviceId, `peering/groups/${groupId}/add_vpc`), { vpc_id: vpcId })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.peeringGroup(serviceId, groupId) }); qc.invalidateQueries({ queryKey: onKeys.peeringGroups(serviceId) }); options?.onSuccess?.(...a); },
  });
}

export function useRemoveVpcFromGroup(serviceId: string, groupId: string, options?: UseMutationOptions<unknown, unknown, string>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vpcId: string) => (await serviceApi.delete(onPath(serviceId, `peering/groups/${groupId}/remove_vpc`), { data: { vpc_id: vpcId } })).data,
    ...options,
    onSuccess: (...a) => { qc.invalidateQueries({ queryKey: onKeys.peeringGroup(serviceId, groupId) }); qc.invalidateQueries({ queryKey: onKeys.peeringGroups(serviceId) }); options?.onSuccess?.(...a); },
  });
}
